import {
  ensureProgramScopedContentRowAtPosition,
  updateProgramScopedContentRowFieldsById,
  type ProgramScopedContentRowFieldPatch,
  type ProgramScopedRowScope,
} from '../services/programScopedContentService';
import type { ProgramMutationNotifyOptions } from '../services/programFixedContentService';
import type { ProgramScopedContentRow } from '../types/domain';

export type ScopedRecordRowRef = {
  /** 화면에서 이 칸과 짝지어진 내용 행 id(있으면 그 행만 고친다) */
  rowId?: string | null;
  /** 같은 스코프 일정 중 이 칸이 몇 번째인지(0부터). 행이 없을 때 이 위치의 행을 찾거나 만든다 */
  position?: number | null;
};

function realRowId(raw: string | null | undefined): string {
  const id = (raw ?? '').trim();
  if (!id) return '';
  if (/^(virtual-|extra:|orphan:|temp-)/.test(id)) return '';
  return id;
}

/**
 * 편제·프로젝트 수업기록/평가/범교과 저장(공통).
 * 1) 행 id가 있으면 그 행의 해당 칸만 고친다(쓰기 1번).
 * 2) 없으면 같은 스코프의 position 번째 행을 찾고, 없으면 그 자리까지 빈 행을 만든 뒤(중복 없이) 고친다.
 */
export async function saveScopedRecordFieldsAtPosition(
  scope: ProgramScopedRowScope,
  ref: ScopedRecordRowRef,
  fields: ProgramScopedContentRowFieldPatch,
  options?: ProgramMutationNotifyOptions
): Promise<ProgramScopedContentRow> {
  const id = realRowId(ref.rowId);
  if (id) {
    const saved = await updateProgramScopedContentRowFieldsById(id, fields, options);
    if (saved) return saved;
  }
  const position = ref.position;
  if (typeof position !== 'number' || !Number.isInteger(position) || position < 0) {
    throw new Error('해당 수업의 프로그램 내용 위치를 찾지 못했습니다. 프로그램 페이지를 새로고침한 뒤 다시 시도해 주세요.');
  }
  const row = await ensureProgramScopedContentRowAtPosition(scope, position, options);
  if (!row?.id) {
    throw new Error('해당 수업의 프로그램 내용 행을 만들지 못했습니다.');
  }
  const saved = await updateProgramScopedContentRowFieldsById(row.id, fields, options);
  if (!saved) {
    throw new Error('수업 기록을 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.');
  }
  return saved;
}

const FILL_IF_EMPTY_FIELDS = [
  'timetable_text',
  'unit_text',
  'page_text',
  'assistant_page_text',
  'current_period_text',
  'total_period_text',
  'material_text',
  'record_cross_curricular_text',
  'record_memo_text',
  'record_evaluation_text',
  'record_class_text',
] as const;

/**
 * 슬라이드·학습지 첨부처럼 "이 칸의 학습내용 행"이 꼭 있어야 할 때(공통).
 * 스코프 안 position 번째 행을 찾고(없으면 그 자리에 하나만 만든다), learning_text 를 넣는다.
 * 다른 칸은 행에 값이 비어 있을 때만 화면 값으로 채운다(다른 기기에서 고친 내용을 덮지 않음).
 */
export async function ensureScopedRowWithLearningAtPosition(
  scope: ProgramScopedRowScope,
  position: number,
  content: { learning_text: string } & Partial<Record<(typeof FILL_IF_EMPTY_FIELDS)[number], string>>,
  options?: ProgramMutationNotifyOptions
): Promise<ProgramScopedContentRow> {
  const safePosition = Number.isInteger(position) && position >= 0 ? position : 0;
  const row = await ensureProgramScopedContentRowAtPosition(scope, safePosition, options);
  if (!row?.id) throw new Error('프로그램 행을 만들지 못했습니다.');
  const patch: ProgramScopedContentRowFieldPatch = {};
  if ((row.learning_text ?? '') !== content.learning_text) patch.learning_text = content.learning_text;
  FILL_IF_EMPTY_FIELDS.forEach((field) => {
    const next = content[field];
    if (typeof next !== 'string' || !next.trim()) return;
    if (((row as unknown as Record<string, unknown>)[field] ?? '') !== '') return;
    (patch as Record<string, unknown>)[field] = next;
  });
  if (!Object.keys(patch).length) return row;
  const saved = await updateProgramScopedContentRowFieldsById(row.id, patch, options);
  if (!saved) throw new Error('프로그램 행을 저장하지 못했습니다.');
  return saved;
}
